import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { PatchConsultationBody } from '@snootsvet/shared'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'

/** Advances a consultation lifecycle (claim, end, finalise). */
export function usePatchConsultation() {
  const api = useOsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: PatchConsultationBody }) => {
      const response = await api.patch('/consultations/:id', { params: { id }, body })
      if (!response.success) throw new Error(response.error.message)
      return response.data.consultation
    },
    onSuccess: (consultation) => {
      // refetchType 'all' ensures the cache is fresh even when the workspace is
      // unmounted (no active observers) — prevents stale finalisedAt after
      // lock → unlock cycles.
      queryClient.invalidateQueries({
        queryKey: ['consultation', consultation.id],
        refetchType: 'all',
      })
      queryClient.invalidateQueries({ queryKey: ['consultations'] })
      queryClient.invalidateQueries({ queryKey: ['patient-timeline', consultation.patientId] })
      // The consultation ↔ appointment link is a back-reference on appointments,
      // so relinking (or finalising) must also refresh appointment lists.
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      queryClient.invalidateQueries({ queryKey: ['patient-appointments', consultation.patientId] })
    },
  })
}
